import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Page from '../../../Page';
import { invoicingApi } from 'src/utils/invoicingApi';
import { showToast } from '../../../../utils/Toast/SnackbarProvider';
import UserInfo from '../../../../utils/Authorization/UserInfo';
import { debounce } from 'src/utils/debounceSearch';
import InvoiceScheduleDrawer from './InvoiceScheduleDrawer';

const SORT_FIELDS = {
  NEXT_INVOICING_DATE: 'next_invoicing_date',
  OUTSTANDING: 'outstanding'
};

const formatMoney = (row, field) => {
  const value = row?.[field];
  if (value === null || value === undefined || value === '') return '—';
  const prefix = row?.currency?.label ? `${row.currency.label} ` : '';
  return `${prefix}${value}`;
};

export default function Invoicing() {
  const userInfo = UserInfo();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed, matches MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState('');
  const [scope, setScope] = useState('mine');
  const [scopeInitialized, setScopeInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // For T6: selected row state + reusable refetch function. The schedule drawer
  // itself is built in Task 6 — do not build it here.
  //
  // B2 final review fix: store only the selected row's id, and derive the row
  // object from the live `rows` list on every render. This way, once a drawer
  // mutation triggers fetchWorklist() and `rows` is refreshed, the drawer's
  // `row` prop (and its read-only TCV-composition panel) picks up the fresh
  // values automatically — no separate re-sync effect, no stale closure over
  // `rows` inside the fetchWorklist useCallback, and no risk of a render loop.
  // If the row is no longer present in a refreshed worklist, selectedRow
  // resolves to null and the drawer closes rather than showing stale/blank data.
  const [selectedRowId, setSelectedRowId] = useState(null);
  const selectedRow = selectedRowId == null ? null : rows.find((r) => r.id === selectedRowId) || null;

  // "Show all deals" defaults on for SUPER_ADMIN. UserInfo() resolves its
  // active_role asynchronously (starts null until the user record loads), so
  // this only applies the default once, the first time active_role becomes
  // available. After that, the user's own toggle takes over.
  useEffect(() => {
    if (!scopeInitialized && userInfo?.active_role) {
      setScope(userInfo.active_role === 'SUPER_ADMIN' ? 'all' : 'mine');
      setScopeInitialized(true);
    }
  }, [userInfo?.active_role, scopeInitialized]);

  const fetchWorklist = useCallback(() => {
    setIsLoading(true);
    const params = {
      scope,
      page: page + 1,
      page_size: rowsPerPage
    };
    if (searchTerm) params.search = searchTerm;
    if (sort) params.sort = sort;

    invoicingApi
      .getWorklist(params)
      .then((response) => {
        const body = response?.data;
        setRows(body?.data || []);
        setTotal(body?.total || 0);
      })
      .catch((err) => {
        showToast(err?.response?.data?.message || 'Failed to load invoicing worklist', 'error');
      })
      .finally(() => setIsLoading(false));
  }, [scope, page, rowsPerPage, searchTerm, sort]);

  useEffect(() => {
    fetchWorklist();
  }, [fetchWorklist]);

  const debouncedSetSearch = useRef(
    debounce((value) => {
      setPage(0);
      setSearchTerm(value);
    }, 350)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const handleScopeChange = (e) => {
    setScopeInitialized(true); // user is now in control of the toggle
    setPage(0);
    setScope(e.target.checked ? 'all' : 'mine');
  };

  const handleSort = (field) => {
    setPage(0);
    setSort((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return field;
      return field;
    });
  };

  const sortDirection = (field) => (sort === `-${field}` ? 'desc' : 'asc');
  const isSortActive = (field) => sort === field || sort === `-${field}`;

  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (row) => {
    setSelectedRowId(row.id);
  };

  return (
    <Page title="Invoicing | CRM">
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Invoicing
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search company"
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <FormControlLabel
              control={<Switch checked={scope === 'all'} onChange={handleScopeChange} />}
              label="Show all deals"
            />
          </Box>
        </Box>

        <Paper>
          {isLoading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>BM</TableCell>
                  <TableCell>TCI</TableCell>
                  <TableCell>Invoiced to date</TableCell>
                  <TableCell sortDirection={isSortActive(SORT_FIELDS.OUTSTANDING) ? sortDirection(SORT_FIELDS.OUTSTANDING) : false}>
                    <TableSortLabel
                      active={isSortActive(SORT_FIELDS.OUTSTANDING)}
                      direction={sortDirection(SORT_FIELDS.OUTSTANDING)}
                      onClick={() => handleSort(SORT_FIELDS.OUTSTANDING)}
                    >
                      Outstanding
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={isSortActive(SORT_FIELDS.NEXT_INVOICING_DATE) ? sortDirection(SORT_FIELDS.NEXT_INVOICING_DATE) : false}
                  >
                    <TableSortLabel
                      active={isSortActive(SORT_FIELDS.NEXT_INVOICING_DATE)}
                      direction={sortDirection(SORT_FIELDS.NEXT_INVOICING_DATE)}
                      onClick={() => handleSort(SORT_FIELDS.NEXT_INVOICING_DATE)}
                    >
                      Next invoicing date
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isLoading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No invoiceable deals found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const outstandingValue = row?.outstanding === null || row?.outstanding === undefined ? null : Number(row.outstanding);
                    const isNegative = outstandingValue !== null && outstandingValue < 0;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{row.company_name}</TableCell>
                        <TableCell>{row.bm ? `${row.bm.first_name} ${row.bm.last_name}` : '—'}</TableCell>
                        <TableCell>{formatMoney(row, 'tci')}</TableCell>
                        <TableCell>{formatMoney(row, 'invoiced_to_date')}</TableCell>
                        <TableCell sx={isNegative ? { color: 'error.main' } : undefined}>
                          {formatMoney(row, 'outstanding')}
                        </TableCell>
                        <TableCell>{row.next_invoicing_date || '—'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>

        {/* ===== T6 INTEGRATION POINT ===== */}
        <InvoiceScheduleDrawer
          open={Boolean(selectedRow)}
          row={selectedRow}
          onClose={() => setSelectedRowId(null)}
          onChanged={fetchWorklist}
        />
      </Container>
    </Page>
  );
}
